import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Search as SearchIcon, Trophy, Gift } from "lucide-react";
import { CoinIcon } from "@/components/Icons";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LiveCard from "@/components/LiveCard";
import BottomNav from "@/components/BottomNav";

const TABS = ["Popular", "New", "Following"];
const CATS = ["Music", "Chat", "Dance", "Comedy", "Gaming", "Talent", "Astro", "PK Battle"];

export default function Home() {
  const location = useLocation();
  const [tab, setTab] = useState("Popular");
  const [cat, setCat] = useState(location.state?.cat || "All");
  const [streams, setStreams] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async (t, c) => {
    setStreams(null);
    try {
      const { data } = await api.get(`/streams?tab=${t.toLowerCase()}&category=${encodeURIComponent(c)}`);
      setStreams(data.streams);
    } catch {
      setStreams([]);
    }
  }, []);

  useEffect(() => {
    load(tab, cat);
  }, [tab, cat, load]);

  const chipCls = (active) =>
    `shrink-0 px-4 py-2 rounded-full text-xs font-bold transition-all ${
      active
        ? "glass-chip-active shadow-[0_0_16px_rgba(255,46,151,0.35)]"
        : "glass-chip text-[#B8B0C9]"
    }`;

  return (
    <div className="min-h-screen max-w-md mx-auto pb-24">
      <header className="flex items-center justify-between px-4 pt-5 pb-4">
        <h1 data-testid="home-logo" className="font-serif-display font-bold text-[30px] leading-none text-[#FFD86B] drop-shadow-[0_2px_14px_rgba(255,216,107,0.3)] select-none">
          Yaara
        </h1>
        <div className="flex items-center gap-2">
          <button data-testid="search-button" onClick={() => navigate("/search")}
            className="glass-chip w-9 h-9 flex items-center justify-center text-white/80 active:scale-95 transition-transform">
            <SearchIcon size={15} />
          </button>
          <button data-testid="leaderboard-button" onClick={() => navigate("/leaderboard")}
            className="glass-chip w-9 h-9 flex items-center justify-center text-[#FFD86B] active:scale-95 transition-transform">
            <Trophy size={15} />
          </button>
          <button data-testid="rewards-button" onClick={() => navigate("/rewards")}
            className="glass-chip w-9 h-9 flex items-center justify-center text-[#FF4D8D] active:scale-95 transition-transform">
            <Gift size={15} />
          </button>
          <button data-testid="header-coins-pill" onClick={() => navigate("/wallet")}
            className="coin-pill px-3.5 py-2 flex items-center gap-1.5 text-sm active:scale-95 transition-transform">
            <CoinIcon size={16} /> {user?.coins?.toLocaleString()}
          </button>
        </div>
      </header>

      <div className="flex gap-2 px-4 mb-4 overflow-x-auto no-scrollbar" data-testid="category-filters">
        {TABS.map((t) => {
          const active = cat === "All" && tab === t;
          return (
            <button key={t} data-testid={`tab-${t.toLowerCase()}`}
              onClick={() => { setTab(t); setCat("All"); }}
              className={chipCls(active)}>
              {t === "Popular" ? "Popular ✦" : t}
            </button>
          );
        })}
        {CATS.map((c) => (
          <button key={c} data-testid={`cat-filter-${c.toLowerCase()}`} onClick={() => setCat(c)}
            className={chipCls(cat === c)}>
            {c}
          </button>
        ))}
      </div>

      <button data-testid="event-banner" onClick={() => navigate("/events")}
        className="mx-4 mb-4 w-[calc(100%-2rem)] rounded-2xl bg-[#1E1830] border border-white/[0.08] flex items-center overflow-hidden active:scale-[0.99] transition-transform">
        <span className="self-stretch w-1.5 shrink-0" style={{ background: "linear-gradient(180deg,#FF9933 0% 33%,#F5F5F0 33% 66%,#138808 66% 100%)" }} />
        <span className="flex-1 text-left px-3 py-3.5 min-w-0">
          <span className="block text-xs font-bold text-white truncate">
            🇮🇳 Azadi Fest — 15 Aug · <span className="text-[#FFD86B]">₹1,00,000 prize pool</span>
          </span>
        </span>
        <span className="shrink-0 mr-3 px-4 py-1.5 rounded-full bg-[#FFD86B] text-[#0B0B14] text-[11px] font-extrabold">Join</span>
      </button>

      <div className="grid grid-cols-2 gap-3 px-4" data-testid="live-grid">
        {streams === null &&
          Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton rounded-3xl aspect-[3/4]" />)}
        {streams?.map((s, i) => (
          <motion.div key={s.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <LiveCard stream={s} />
          </motion.div>
        ))}
        {streams?.length === 0 && (
          <div className="col-span-2 text-center py-16 text-[#6E6683] text-sm">
            {tab === "Following" && cat === "All" ? "Follow streamers to see them here 💜" : "No live streams right now"}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}
